
import React, { useState } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { useAdminData } from '@/hooks/useAdminData';
import {
  QUICKSCAN_PRIORITY_SCALE,
  orderQuickscanPriorities,
} from '@/lib/quickscan-priorities';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  LogOut, 
  Search, 
  Trash2, 
  Eye, 
  Download,
  RefreshCw,
  Building2,
  Calendar,
  Euro,
  BarChart,
  Filter,
  Users
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import AdminSubmissionDetail from './AdminSubmissionDetail';
import AdminAnalytics from './AdminAnalytics';
import AdminKPICards from './AdminKPICards';
import AdminFilters from './AdminFilters';
import BusinessInsights from './BusinessInsights';
import LeadsManagement from './LeadsManagement';

const AdminDashboard = () => {
  const { logout } = useAdmin();
  const { submissions, loading, refetch, deleteSubmission } = useAdminData();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<string | null>(null);
  const [filteredSubmissions, setFilteredSubmissions] = useState(submissions);

  // Update filtered submissions when submissions change
  React.useEffect(() => {
    setFilteredSubmissions(submissions);
  }, [submissions]);

  const displayedSubmissions = filteredSubmissions.filter(submission => 
    submission.business_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    submission.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    submission.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatExpansionDetails = (details: Record<string, any>) => {
    const formatted: string[] = [];
    
    Object.entries(details).forEach(([planType, data]) => {
      switch (planType) {
        case 'location':
          formatted.push(`Locatie: ${data.location || 'N/A'}`);
          formatted.push(`Eigendom: ${data.ownership === 'buy' ? 'Kopen' : 'Huren'}`);
          formatted.push(`Oppervlakte: ${data.surfaceArea || 'N/A'} m²`);
          formatted.push(`Doel: ${data.purpose || 'N/A'}`);
          break;
        case 'sustainability':
          formatted.push(`Certificeringen: ${data.certifications?.join(', ') || 'Geen'}`);
          formatted.push(`Doelstellingen: ${data.targets?.join(', ') || 'Geen'}`);
          formatted.push(`Tijdlijn: ${data.timeline || 'N/A'} jaar`);
          formatted.push(`CO2 reductie doel: ${data.co2ReductionTarget || 'N/A'}%`);
          formatted.push(`Budget: ${data.budget || 'N/A'}`);
          break;
        case 'growth':
          formatted.push(`Groei percentage: ${data.growthPercentage || 'N/A'}%`);
          formatted.push(`Tijdsperiode: ${data.timeframe || 'N/A'}`);
          formatted.push(`Groei types: ${data.growthTypes?.join(', ') || 'Geen'}`);
          formatted.push(`Extra werknemers: ${data.additionalEmployees || 'N/A'}`);
          break;
        case 'equipment':
          formatted.push(`Equipment types: ${data.equipmentTypes?.join(', ') || 'Geen'}`);
          formatted.push(`Energieverbruik: ${data.energyConsumption || 'N/A'} kWh`);
          formatted.push(`Vermogen: ${data.powerConsumption || 'N/A'} kW`);
          formatted.push(`Aantal: ${data.quantity || 'N/A'}`);
          formatted.push(`Bedrijfsuren: ${data.operatingHours || 'N/A'} uur/dag`);
          formatted.push(`Implementatie: ${data.timeline || 'N/A'}`);
          break;
      }
    });
    
    return formatted.join(' | ');
  };

  // De export noemde de rauwe sleutel en deelde door 5, terwijl de schuifjes
  // 1 t/m 10 lopen — een 10 las in de CSV als "10/5".
  const formatPriorities = (priorities: Record<string, number>) => {
    return orderQuickscanPriorities(priorities)
      .map(({ label, value }) => `${label}: ${value}/${QUICKSCAN_PRIORITY_SCALE.max}`)
      .join(' | ');
  };

  const exportToCSV = () => {
    const csvData = submissions.map(sub => ({
      'Submission ID': sub.id,
      'Bedrijfsnaam': sub.company_name || '',
      'Email': sub.email || '',
      'Bedrijfstype': sub.business_type,
      'Aantal werknemers': sub.employee_count,
      'Maandelijkse energiekosten (€)': sub.monthly_energy_costs,
      'Huidige oplossingen': sub.current_solutions.join(', ') || '',
      'Energie piekuren': sub.energy_peak_hours.map(h => `${h}:00`).join(', ') || '',
      'Uitbreidingsplannen': sub.expansion_plans.join(', ') || '',
      'Uitbreidingsdetails': formatExpansionDetails(sub.expansion_details),
      'Prioriteiten': formatPriorities(sub.priorities),
      'Energy Potential Score (%)': sub.energy_potential_score || '',
      'Jaarlijkse besparingen (€)': sub.yearly_annual_savings || '',
      'CO2 reductie (ton)': sub.co2_reduction || '',
      'ROI periode (maanden)': sub.roi_months || '',
      'Aanbevelingen': sub.recommendations.map((rec, index) => `${index + 1}. ${rec}`).join(' | ') || '',
      'Aangemaakt op': format(new Date(sub.created_at), 'dd-MM-yyyy HH:mm', { locale: nl }),
      'Laatst bijgewerkt': format(new Date(sub.created_at), 'dd-MM-yyyy HH:mm', { locale: nl })
    }));

    // Create CSV content with proper escaping
    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => 
        headers.map(header => {
          const value = row[header as keyof typeof row];
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',')
      )
    ].join('\n');

    // Add BOM for proper Excel UTF-8 support
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quickscan-submissions-volledig-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (selectedSubmission) {
    return (
      <AdminSubmissionDetail
        submissionId={selectedSubmission}
        onBack={() => setSelectedSubmission(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Admin Dashboard
              </h1>
              <p className="text-gray-600">Quickscan Submissions Beheer & Analytics</p>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={refetch} disabled={loading}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Vernieuwen
              </Button>
              <Button variant="outline" onClick={exportToCSV} disabled={submissions.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button variant="outline" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Uitloggen
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-gray-600">Laden...</p>
          </div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-5 lg:w-[800px]">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Overzicht
              </TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-2">
                <BarChart className="h-4 w-4" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="submissions" className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Submissions
              </TabsTrigger>
              <TabsTrigger value="leads" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Leads
              </TabsTrigger>
              <TabsTrigger value="insights" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Insights
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <AdminKPICards submissions={submissions} />
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Submissions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Recente Submissions</CardTitle>
                    <CardDescription>Laatste 5 submissions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {submissions.slice(0, 5).map(submission => (
                        <div key={submission.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex-1">
                            <div className="font-semibold text-sm">{submission.company_name || 'Geen bedrijfsnaam'}</div>
                            <div className="text-xs text-gray-500">{submission.business_type}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium">€{submission.monthly_energy_costs}</div>
                            <div className="text-xs text-gray-500">
                              {format(new Date(submission.created_at), 'dd MMM', { locale: nl })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle>Snelle Statistieken</CardTitle>
                    <CardDescription>Belangrijkste metrics</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Hoogste Energy Score</span>
                      <Badge variant="default">
                        {Math.max(...submissions.map(s => s.energy_potential_score || 0))}%
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Totaal Bedrijfstypes</span>
                      <Badge variant="outline">
                        {new Set(submissions.map(s => s.business_type)).size}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Met Uitbreidingsplannen</span>
                      <Badge variant="secondary">
                        {submissions.filter(s => s.expansion_plans.length > 0).length}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="analytics" className="space-y-6">
              <AdminAnalytics submissions={submissions} />
            </TabsContent>

            <TabsContent value="submissions" className="space-y-6">
              <AdminFilters 
                submissions={submissions}
                onFilterChange={setFilteredSubmissions}
              />
              
              {/* Submissions Table */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Submissions Lijst</CardTitle>
                      <CardDescription>
                        {displayedSubmissions.length} van {submissions.length} submissions
                      </CardDescription>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Search className="h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Zoek in huidige resultaten..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-64"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Bedrijf</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Energiekosten</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Datum</TableHead>
                          <TableHead className="text-right">Acties</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayedSubmissions.map((submission) => (
                          <TableRow key={submission.id} className="hover:bg-gray-50/50">
                            <TableCell className="font-medium">
                              <div>
                                <div className="font-semibold">
                                  {submission.company_name || 'Geen bedrijfsnaam'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {submission.employee_count} werknemers
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {submission.email || 'Geen email'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {submission.business_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              €{submission.monthly_energy_costs.toLocaleString()}/maand
                            </TableCell>
                            <TableCell>
                              {submission.energy_potential_score ? (
                                <Badge 
                                  variant={submission.energy_potential_score >= 70 ? "default" : "secondary"}
                                >
                                  {submission.energy_potential_score}%
                                </Badge>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {format(new Date(submission.created_at), 'dd MMM yyyy', { locale: nl })}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setSelectedSubmission(submission.id)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="outline" size="sm">
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Submission verwijderen?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Deze actie kan niet ongedaan gemaakt worden. Alle data van deze submission wordt permanent verwijderd.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => deleteSubmission(submission.id)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Verwijderen
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    
                    {displayedSubmissions.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        {searchTerm || filteredSubmissions.length < submissions.length 
                          ? 'Geen submissions gevonden voor deze filters.' 
                          : 'Nog geen submissions beschikbaar.'
                        }
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="leads" className="space-y-6">
              <LeadsManagement />
            </TabsContent>

            <TabsContent value="insights" className="space-y-6">
              <BusinessInsights submissions={submissions} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
